import { CenterSpinner } from '@/components/ats';
import { useDateFormat } from '@/hooks';
import { SlotsPreview } from '@/components/shared/interview-scheduler/components/SlotsPreview';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { KText } from '@/ui-library/components';
import {
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDown, Stars03 } from '@kula-ai/icons-core';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const optionsConfig = (nextWeek) => [
  {
    value: 1,
    label: 'Next weekday',
  },
  {
    value: 2,
    label: 'Next 2 weekdays',
  },
  {
    value: 3,
    label: 'Next 3 weekdays',
  },
  {
    value: 4,
    label: 'Next 4 weekdays',
  },
  {
    value: 5,
    label: 'Next 5 weekdays',
  },
  {
    value: 'next_week',
    label: 'Next week (' + nextWeek + ')',
  },
];

interface SchedulerState {
  [key: string]: {
    isAllChecked: boolean;
    days: {
      [dayState: string]: {
        isSelected: boolean;
        slotsGenerated: any[];
        removedSlots: any[];
        customSlots: any[];
      };
    };
  };
}

function processSelectedSlots(data: SchedulerState) {
  const result = {};
  const values = Object.values(data || {});

  values.forEach((item) => {
    const days = item?.days;

    if (days) {
      Object.entries(days).forEach(([dayKey, dayValue]) => {
        if (dayValue.isSelected && dayValue.slotsGenerated) {
          const final = [];

          const removedSlotIds = dayValue.removedSlots || [];
          const filteredSlots = dayValue.slotsGenerated.filter(
            (slot) => !removedSlotIds.includes(slot.id) && slot?.start
          );

          final.push(...filteredSlots);

          // Custom slots logic
          const validCustomSlots = dayValue.customSlots.filter((slot) => {
            const cleanedId = slot.id.split('custom')[1];
            if (dayValue.removedSlots.includes(cleanedId) && slot?.start)
              return true;
            const isDuplicate = dayValue.slotsGenerated.find(
              (i) => i?.id === cleanedId
            );
            return !isDuplicate && slot?.start;
          });

          final.push(...validCustomSlots);

          if (final.length > 0) {
            result[dayKey] = final.map((slot) => ({
              start_time: slot.start,
              end_time: slot.end,
            }));
          }
        } else {
          if (dayValue?.customSlots?.length > 0)
            result[dayKey] = dayValue?.customSlots?.map((slot) => ({
              start_time: slot.start,
              end_time: slot.end,
            }));
        }
      });
    }
  });

  return result;
}
const getTotalSlotsGenerated = (data: SchedulerState): number => {
  let totalSlots = 0;

  if (!data) return totalSlots; // Avoid unnecessary iterations

  Object.values(data).forEach((week) => {
    if (!week?.days) return;

    Object.values(week.days).forEach((day) => {
      if (day?.isSelected) {
        const generatedCount = day.slotsGenerated?.length ?? 0;
        const removedCount = day.removedSlots?.length ?? 0;

        const validCustomSlots = day.customSlots.filter((slot) => {
          const cleanedId = slot.id.split('custom')[1];
          if (day.removedSlots.includes(cleanedId)) return true;
          const isDuplicate = day.slotsGenerated.find(
            (i) => i?.id === cleanedId
          );
          return !isDuplicate;
        });

        totalSlots += generatedCount + validCustomSlots.length - removedCount;
      } else {
        totalSlots += day.customSlots.length;
      }
    });
  });

  return totalSlots;
};

// Function to generate dynamic options based on available days
const generateDynamicOptions = (availableDays, timezone, dateFormat) => {
  // Sort available days chronologically
  const sortedDays = [...availableDays].sort((a, b) => {
    return moment
      .tz(a, dateFormat.label, timezone)
      .diff(moment.tz(b, dateFormat.label, timezone));
  });

  if (sortedDays.length === 0) {
    return optionsConfig; // Return default options if no days available
  }

  // Format dates for display
  const formattedDays = sortedDays.map((day) => {
    const date = moment.tz(day, dateFormat.label, timezone);
    return {
      date: date,
      formatted: date.format('D MMM'), // e.g., "7 Apr"
    };
  });

  // Generate dynamic options based on available days
  const dynamicOptions = [];

  // Single day option
  if (formattedDays.length >= 1) {
    dynamicOptions.push({
      value: 1,
      label: `Next weekday (${formattedDays[0].formatted})`,
    });
  }

  // 2-5 day options
  for (let i = 2; i <= Math.min(5, formattedDays.length); i++) {
    const lastIndex = i - 1;
    dynamicOptions.push({
      value: i,
      label: `Next ${i} weekdays (${formattedDays[0].formatted} to ${formattedDays[lastIndex].formatted})`,
    });
  }

  // Next week option
  if (formattedDays.length > 0) {
    // Calculate the date range for the next week
    const firstDay = formattedDays[0].date;
    const lastPossibleDay = firstDay.clone().add(6, 'days');

    // Find the last available day within the next week
    let lastAvailableDay = formattedDays[0].formatted;
    for (let i = 1; i < formattedDays.length; i++) {
      if (formattedDays[i].date.isSameOrBefore(lastPossibleDay)) {
        lastAvailableDay = formattedDays[i].formatted;
      } else {
        break;
      }
    }

    dynamicOptions.push({
      value: 'next_week',
      label: `Next week (${formattedDays[0].formatted} to ${lastAvailableDay})`,
    });
  }

  return dynamicOptions;
};

const generateFixedOptions = (timezone) => {
  // Get current date in the specified timezone
  const now = moment().tz(timezone);

  // Find the next weekday (skip weekends)
  const nextWeekday = now.clone().add(1, 'day');
  while (nextWeekday.day() === 0 || nextWeekday.day() === 6) {
    nextWeekday.add(1, 'day');
  }

  // Format date for display
  const formatDate = (date) => date.format('D MMM'); // e.g., "7 Apr"

  // Generate fixed options
  const fixedOptions = [];

  // Next weekday option
  fixedOptions.push({
    value: 1,
    label: `Next weekday`,
    subLabel: formatDate(nextWeekday),
  });

  // 2-5 day options
  for (let i = 2; i <= 5; i++) {
    const lastDay = nextWeekday.clone();
    let daysAdded = 0;

    // Add required number of weekdays (skip weekends)
    while (daysAdded < i - 1) {
      lastDay.add(1, 'day');
      if (lastDay.day() !== 0 && lastDay.day() !== 6) {
        daysAdded++;
      }
    }

    fixedOptions.push({
      value: i,
      label: `Next ${i} weekdays`,
      subLabel: `${formatDate(nextWeekday)} to ${formatDate(lastDay)}`,
    });
  }

  // Find next Sunday and Saturday for "Next week" option
  const nextSunday = now.clone().add(1, 'day');
  while (nextSunday.day() !== 0) {
    nextSunday.add(1, 'day');
  }

  const nextSaturday = nextSunday.clone().add(6, 'day');

  fixedOptions.push({
    value: 'next_week',
    label: `Next week`,
    subLabel: `${formatDate(nextSunday)} to ${formatDate(nextSaturday)}`,
  });

  return fixedOptions;
};

// Modified RenderOptions component to use dynamic options
const RenderOptions = ({
  popoverActions,
  onSelect = undefined,
  updateSharedData,
  availableDays,
  timezone,
  dateFormat,
  nextWeek,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dynamicOptions = generateDynamicOptions(
    availableDays,
    timezone,
    dateFormat
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fixedOptions = generateFixedOptions(timezone);

  return (
    <Flex flexDir="column">
      {optionsConfig(nextWeek).map((option) => (
        <KText
          key={option.value}
          p="12px"
          minW="200px"
          cursor="pointer"
          borderBottom="1px solid"
          borderColor="secondary-100"
          _hover={{
            bgColor: 'secondary-25',
          }}
          onClick={() => {
            updateSharedData({
              findTimeValue: option?.value,
            });
            onSelect?.(option);
            popoverActions?.onClose();
          }}
        >
          {option.label}
          {/* {option?.subLabel && (
            <KText fontSize="12px" as="span">
              {' '}
              ({option?.subLabel})
            </KText>
          )} */}
        </KText>
      ))}
    </Flex>
  );
};

// Modified SchFindTimeField component to include the availableDays prop
export const SchFindTimeField = ({
  updateSharedData,
  interviewDataRef,
  registerForUpdates,
}) => {
  const iframeActions = useDisclosure();
  const popoverActions = useDisclosure();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [localWeekAvailability, setLocalWeekAvailability] = useState<boolean>(
    interviewDataRef?.current?.weekAvailability ?? false
  );
  const [localLoadingEvents, setLoadingEvents] = useState<boolean>(
    interviewDataRef?.current?.loadingEvents ?? false
  );
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const { dateFormat } = useDateFormat();
  const [minimumNoticeEnabled, setMinimumNoticeEnabled] = useState<boolean>(
    interviewDataRef?.current?.minimum_notice_enabled ?? false
  );
  const [minimumNoticeTimeHours, setMinimumNoticeTimeHours] = useState<number>(
    () => {
      const minimumNotice = interviewDataRef?.current?.minimum_notice_config;
      if (!minimumNotice) return 0;

      if (minimumNotice.value && minimumNotice.type) {
        return minimumNotice.type === 'days'
          ? minimumNotice.value * 24
          : minimumNotice.value;
      }

      return 0;
    }
  );

  const formatMinimumNoticeTime = (): string => {
    const minimumNotice = interviewDataRef?.current?.minimum_notice_config;
    if (!minimumNotice || !minimumNotice.value || !minimumNotice.type) {
      return '0 hours';
    }

    const { value, type } = minimumNotice;
    if (type === 'days') {
      return `${value} day${value === 1 ? '' : 's'}`;
    } else {
      return `${value} hour${value === 1 ? '' : 's'}`;
    }
  };

  useEffect(() => {
    // calculate on mount
    setTotalCount((prev) => {
      const { schedulerState = {} } = interviewDataRef?.current ?? {};
      const total = getTotalSlotsGenerated(schedulerState);
      if (prev !== total) {
        return total;
      }
      return prev;
    });

    // Get available days on mount
    const currentWeekRange = interviewDataRef?.current?.currentWeek;
    const schedulerState = interviewDataRef?.current?.schedulerState || {};
    const weekState = schedulerState[currentWeekRange] || { days: {} };

    // Find available days (days with checkboxes that aren't disabled)
    const availableDaysArray = Object.keys(weekState.days).filter((dateKey) => {
      const dayState = weekState.days[dateKey];
      return (
        dayState &&
        (dayState.slotsGenerated?.length > 0 ||
          dayState.customSlots?.length > 0)
      );
    });

    setAvailableDays(availableDaysArray);
  }, [interviewDataRef]);

  useEffect(() => {
    const unregisterSchedulerState = registerForUpdates(
      '[schedulerState, interviewers, currentWeek]',
      (data) => {
        // Sync "X slots selected" from schedulerState when it changes.
        const { schedulerState = {} } = data?.fullData ?? {};
        const total = getTotalSlotsGenerated(schedulerState);
        setTotalCount((prev) => (prev !== total ? total : prev));

        // Update available days when scheduler state changes
        const currentWeekRange = data?.fullData?.currentWeek;
        const weekState = schedulerState[currentWeekRange] || { days: {} };

        const availableDaysArray = Object.keys(weekState.days).filter(
          (dateKey) => {
            const dayState = weekState.days[dateKey];
            return (
              dayState &&
              (dayState.slotsGenerated?.length > 0 ||
                dayState.customSlots?.length > 0)
            );
          }
        );

        setAvailableDays(availableDaysArray);
      }
    );

    const unregisterNoSlots = registerForUpdates('trackCountTotal', (data) => {
      const { trackCountTotal } = data?.fullData ?? {};
      setTotalCount(trackCountTotal);
    });

    const unregisterLoadingEvents = registerForUpdates(
      'loadingEvents',
      (data) => {
        setLoadingEvents(data?.fullData?.loadingEvents ?? false);
      }
    );

    const unregisterWeekAvailability = registerForUpdates(
      'weekAvailability',
      (data) => {
        const { weekAvailability, currentWeek } = data?.fullData ?? {};
        setLocalWeekAvailability(weekAvailability?.[currentWeek]);
      }
    );

    const unregisterMinimumNoticeEnabled = registerForUpdates(
      'minimum_notice_enabled',
      (data) => {
        const { minimum_notice_enabled } = data?.fullData ?? {};
        setMinimumNoticeEnabled(minimum_notice_enabled);
      }
    );

    const unregisterMinimumNoticeTimeHours = registerForUpdates(
      'minimum_notice_config',
      (data) => {
        const { minimum_notice_config } = data?.fullData ?? {};
        const minimumNoticeTimeHours = (() => {
          if (!minimum_notice_config) return 0;

          if (minimum_notice_config.value && minimum_notice_config.type) {
            return minimum_notice_config.type === 'days'
              ? minimum_notice_config.value * 24
              : minimum_notice_config.value;
          }

          return 0;
        })();
        setMinimumNoticeTimeHours(minimumNoticeTimeHours);
      }
    );

    return () => {
      unregisterNoSlots();
      unregisterWeekAvailability();
      unregisterSchedulerState();
      unregisterLoadingEvents();
      unregisterMinimumNoticeEnabled();
      unregisterMinimumNoticeTimeHours();
    };
  }, [registerForUpdates]);

  const hasSomeSlots = totalCount > 0;

  const onPreview = () => {
    if (!hasSomeSlots) return;
    iframeActions.onOpen();
  };

  const onClear = () => {
    if (!hasSomeSlots) return;
    updateSharedData({
      resetAllSlots: true,
      findTimeValue: '',
    });
  };

  const renderTopSection = () => {
    return (
      <Flex w="100%" justifyContent={'space-between'}>
        <KText variant="heading_4" userSelect="none">
          {totalCount} slots selected
        </KText>
        <Flex gap="12px">
          <KText
            cursor={hasSomeSlots ? 'pointer' : 'not-allowed'}
            onClick={onPreview}
            variant="heading_4"
            color="primary-500"
            opacity={hasSomeSlots ? 1 : 0.5}
          >
            Preview
          </KText>
          <KText
            cursor={hasSomeSlots ? 'pointer' : 'not-allowed'}
            onClick={onClear}
            variant="heading_4"
            color="primary-500"
            opacity={hasSomeSlots ? 1 : 0.5}
          >
            Clear
          </KText>
        </Flex>
      </Flex>
    );
  };

  const renderWeekButton = () => {
    const timezone = interviewDataRef?.current?.datesSet?.timeZone;
    const now = moment().tz(timezone).startOf('week');
    const currentWeek =
      now.format('D MMM') + ' - ' + now.add(6, 'days').format('D MMM');

    const nextNow = moment().tz(timezone).startOf('week');
    const nextWeek =
      nextNow.clone().add(7, 'days').format('D MMM') +
      ' - ' +
      nextNow.clone().add(13, 'days').format('D MMM');

    return (
      <Flex
        justifyContent="space-between"
        border="1px solid"
        borderRadius="6px"
        borderColor="primary-500"
        bg="base-white"
        position="relative"
        opacity={localWeekAvailability ? 1 : 0.5}
        cursor={localWeekAvailability ? 'default' : 'not-allowed'}
      >
        <Flex
          alignItems="center"
          p="4px"
          gap="md"
          w="100%"
          justifyContent="center"
          borderLeftRadius="6px"
          _hover={{
            bgColor: 'primary-25',
          }}
          cursor={localWeekAvailability ? 'pointer' : 'not-allowed'}
          onClick={() => {
            if (!localWeekAvailability) return;
            updateSharedData({
              findTimeValue: 'this_week',
            });
          }}
        >
          <Stars03
            size="1em"
            color="var(--kula-colors-primary-500)"
            className="kula-icon"
          />
          <KText color="primary-500" userSelect="none">
            Find time ({currentWeek})
          </KText>
        </Flex>

        <Menu
          isOpen={popoverActions?.isOpen}
          onClose={() => popoverActions?.onClose()}
          closeOnBlur
        >
          <MenuButton
            disabled={!localWeekAvailability}
            cursor={localWeekAvailability ? 'cursor' : 'not-allowed'}
            _hover={{
              bgColor: 'primary-25',
            }}
            borderRightRadius="6px"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              popoverActions?.onToggle();
            }}
          >
            <Flex w="30px" alignItems="center" justifyContent="center" p="4px">
              <ChevronDown
                size="1em"
                color="var(--kula-colors-primary-500)"
                className="kula-icon"
              />
            </Flex>
          </MenuButton>
          <MenuList
            p="0"
            border="1px solid"
            borderRadius="6px"
            borderColor={'var(--kula-colors-secondary-100) !important'}
            minW="220px"
          >
            <RenderOptions
              popoverActions={popoverActions}
              updateSharedData={updateSharedData}
              availableDays={availableDays}
              timezone={timezone}
              dateFormat={dateFormat}
              nextWeek={nextWeek}
            />
          </MenuList>
        </Menu>
        <Divider
          position="absolute"
          color="primary-500"
          orientation="vertical"
          right="29px"
          top="0"
          opacity="1"
        />
      </Flex>
    );
  };

  const {
    interviewName,
    bookedSlot,
    datesSet,
    schedulerState,
    candidate,
    interviewLocation,
  } = interviewDataRef?.current ?? {};
  const currUser = useSelector(selectCurrUser);

  const iFrameData = {
    src: 'kula',
    styleProps: {},
    name: interviewName,
    duration: bookedSlot?.duration,
    timeZone: datesSet?.timeZone,
    location_type: interviewLocation ?? 'google_meet',
    contact_name: candidate?.name ?? 'Contact name placeholder',
    contact_email: candidate?.email ?? 'Contact email placeholder',
    slots: processSelectedSlots(schedulerState),
    format: currUser?.date_fmt,
  };

  return (
    <Flex
      borderRadius="6px"
      bg="primary-25"
      flexDir="column"
      gap="6px"
      p="14px"
      w="100%"
      position="relative"
      justifyContent="space-between"
    >
      {localLoadingEvents && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          bg="rgba(248, 249, 252, 0.4)"
          zIndex="10"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="not-allowed"
        >
          <CenterSpinner spinnerProps={{ color: '#797DFF' }} />
        </Box>
      )}
      {renderTopSection()}
      {renderWeekButton()}
      {minimumNoticeEnabled && minimumNoticeTimeHours > 0 && (
        <Box
          borderRadius="6px"
          border="1px solid"
          borderColor="secondary-100"
          bg="base-white"
          p="12px"
          mt="8px"
        >
          <KText color="secondary-700">
            <strong>Note:</strong> Candidates can only book interviews starting{' '}
            <strong>{formatMinimumNoticeTime()}</strong> from now. Select slots
            beyond this timeframe to give candidates options.
          </KText>
        </Box>
      )}
      {iframeActions?.isOpen && (
        <SlotsPreview
          previewData={iFrameData}
          onClose={iframeActions.onClose}
        />
      )}
    </Flex>
  );
};
